<template>
  <div class="auth-page">
    <!-- Background elements -->
    <div class="bg-glow"></div>
    <div class="bg-grid"></div>
    
    <!-- Navigation -->
    <nav class="navbar">
      <router-link to="/" class="nav-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <router-link to="/login" class="btn-login">Login</router-link>
    </nav>

    <!-- Verify Email Content -->
    <div class="auth-container">
      <div class="auth-card">
        <!-- Loading State -->
        <div v-if="loading" class="status-content">
          <div class="spinner"></div>
          <p>Verifying your email...</p>
        </div>

        <!-- Success State -->
        <div v-else-if="success" class="status-content">
          <div class="status-icon success">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1>Email Verified!</h1>
          <p>Your email has been successfully verified. You can now log in to your account.</p>
          <router-link to="/login" class="btn-primary">Go to Login</router-link>
        </div>

        <!-- Resend Success State -->
        <div v-else-if="resendSuccess" class="status-content">
          <div class="status-icon success">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 2L11 13"></path>
              <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
            </svg>
          </div>
          <h1>Email Sent!</h1>
          <p>A new verification link has been sent to <strong>{{ resendEmail }}</strong>. Please check your inbox.</p>
          <router-link to="/login" class="btn-secondary">Back to Login</router-link>
        </div>

        <!-- Error State with Resend Option -->
        <div v-else class="status-content">
          <div class="status-icon error">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h1>Verification Failed</h1>
          <p>{{ error }}</p>
          
          <div class="resend-section">
            <p class="resend-text">Enter your email to receive a new verification link:</p>
            <div class="form-group">
              <input 
                type="email" 
                v-model="resendEmail" 
                placeholder="you@example.com"
              />
            </div>
            <button 
              @click="resendVerification" 
              class="btn-primary" 
              :disabled="resendLoading || !resendEmail"
            >
              {{ resendLoading ? 'Sending...' : 'Resend Verification' }}
            </button>
            <p v-if="resendError" class="resend-error">{{ resendError }}</p>
          </div>
          
          <router-link to="/login" class="back-link">Back to Login</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios, { formatApiErrorMessage } from '@/axios';
import AppLogo from '@/components/AppLogo.vue';

export default {
  name: 'VerifyEmail',
  components: { AppLogo },
  data() {
    return {
      loading: true,
      success: false,
      error: null,
      resendEmail: '',
      resendLoading: false,
      resendSuccess: false,
      resendError: null,
    };
  },
  computed: {
    token() {
      return this.$route.query.token;
    },
  },
  async mounted() {
    if (!this.token) {
      this.loading = false;
      this.error = 'Invalid verification link. No token provided.';
      return;
    }
    
    try {
      await axios.post('/api/auth/verify-email', { token: this.token });
      this.success = true;
    } catch (err) {
      this.error = formatApiErrorMessage(err, 'Verification failed. The link may have expired or already been used.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async resendVerification() {
      if (!this.resendEmail) return;
      
      this.resendLoading = true;
      this.resendError = null;
      
      try {
        await axios.post('/api/auth/resend-verification', { email: this.resendEmail });
        this.resendSuccess = true;
      } catch (err) {
        this.resendError = formatApiErrorMessage(err, 'Failed to send verification email. Please try again.');
      } finally {
        this.resendLoading = false;
      }
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.auth-page {
  min-height: 100vh;
  background: #0a0a0f;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
  filter: blur(60px);
  pointer-events: none;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

/* Navbar */
.navbar {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
}

.nav-logo {
  text-decoration: none;
}

.logo-img {
  height: 56px;
}

.btn-login {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s;
}

.btn-login:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.2);
}

/* Auth Container */
.auth-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  backdrop-filter: blur(10px);
}

/* Status Content */
.status-content {
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(139, 92, 246, 0.2);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.status-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.status-icon.success {
  background: rgba(34, 197, 94, 0.1);
  color: #4ade80;
}

.status-icon.error {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
}

.status-content h1 {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.75rem;
}

.status-content p {
  color: #71717a;
  font-size: 15px;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.status-content strong {
  color: #a1a1aa;
}

/* Resend Section */
.resend-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
}

.resend-text {
  color: #a1a1aa;
  font-size: 14px;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  transition: all 0.2s;
}

.form-group input::placeholder {
  color: #52525b;
}

.form-group input:focus {
  outline: none;
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.resend-error {
  color: #f87171;
  font-size: 14px;
  margin-top: 1rem;
}

/* Buttons */
.btn-primary {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: block;
  text-align: center;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(139, 92, 246, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  width: 100%;
  padding: 14px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: block;
  text-align: center;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
}

.back-link {
  display: block;
  color: #71717a;
  text-decoration: none;
  font-size: 14px;
  margin-top: 1rem;
  transition: color 0.2s;
}

.back-link:hover {
  color: #a1a1aa;
}
</style>
